export const takeoffIssueLabels = {
  "wrong-asset": "Wrong asset match",
  "wrong-work-status": "Wrong work status",
  "wrong-scope": "Wrong repair scope",
  "missing-part": "Missing part",
  "wrong-part": "Wrong part or quantity",
  "wrong-supplier": "Wrong supplier suggestion",
  "wrong-hours": "Wrong labour hours",
  "source-reading": "Source text read incorrectly",
  other: "Other issue",
} as const;

export type TakeoffIssue = keyof typeof takeoffIssueLabels;
export type FindingAssessment = {
  verdict: "accurate" | "correction";
  issues: TakeoffIssue[];
  note: string;
};
export type MissedFinding = {
  id: string;
  assetId: string;
  sourceRows: number[];
  description: string;
};
export type TakeoffFeedback = {
  assessments: Record<string, FindingAssessment>;
  missedFindings: MissedFinding[];
};
export type TakeoffFeedbackArchive = {
  analysisId: string;
  archivedAt: string;
  proposedFindingIds: string[];
  feedback: TakeoffFeedback;
};
export type TakeoffScorecard = {
  proposed: number;
  assessed: number;
  accurate: number;
  corrections: number;
  missed: number;
  issueCounts: Record<TakeoffIssue, number>;
};

export function emptyTakeoffFeedback(): TakeoffFeedback {
  return { assessments: {}, missedFindings: [] };
}

export function archiveTakeoffFeedback(history: TakeoffFeedbackArchive[], analysisId: string, findingIds: string[], feedback: TakeoffFeedback, archivedAt: string): TakeoffFeedbackArchive[] {
  if (!Object.keys(feedback.assessments).length && !feedback.missedFindings.length) return history;
  return [...history, { analysisId, archivedAt, proposedFindingIds: [...findingIds], feedback: structuredClone(feedback) }];
}

export function parseSourceRows(value: string): number[] {
  const tokens = value.split(/[\s,;]+/).filter(Boolean);
  const rows = tokens.map((item) => Number(item));
  if (!rows.length || tokens.some((item) => !/^\d+$/.test(item)) || rows.some((row) => !Number.isSafeInteger(row) || row < 1) || new Set(rows).size !== rows.length) {
    throw new Error("Enter one or more distinct source row numbers, separated by commas.");
  }
  return rows;
}

export function recordMissedFinding(feedback: TakeoffFeedback, input: MissedFinding): TakeoffFeedback {
  const assetId = input.assetId.trim();
  const description = input.description.trim();
  if (!assetId || !description || !input.sourceRows.length || input.sourceRows.some((row) => !Number.isSafeInteger(row) || row < 1)) {
    throw new Error("Enter the asset, source row and missed finding.");
  }
  if (feedback.missedFindings.some((item) => item.id === input.id || (item.assetId.toLowerCase() === assetId.toLowerCase() && item.description.toLowerCase() === description.toLowerCase() && item.sourceRows.join(",") === input.sourceRows.join(",")))) throw new Error("This missed finding has already been recorded.");
  return { ...feedback, missedFindings: [...feedback.missedFindings, { ...input, assetId, description, sourceRows: [...input.sourceRows] }] };
}

export function scoreTakeoffFeedback(findingIds: string[], feedback: TakeoffFeedback): TakeoffScorecard {
  const issueCounts = Object.fromEntries(Object.keys(takeoffIssueLabels).map((issue) => [issue, 0])) as Record<TakeoffIssue, number>;
  let assessed = 0;
  let accurate = 0;
  let corrections = 0;
  for (const id of new Set(findingIds)) {
    const assessment = feedback.assessments[id];
    if (!assessment) continue;
    if (assessment.verdict === "accurate") {
      assessed += 1;
      accurate += 1;
    } else if (assessment.verdict === "correction" && assessment.issues.length) {
      assessed += 1;
      corrections += 1;
      for (const issue of new Set(assessment.issues)) if (issue in issueCounts) issueCounts[issue] += 1;
    }
  }
  return { proposed: new Set(findingIds).size, assessed, accurate, corrections, missed: feedback.missedFindings.length, issueCounts };
}
